<template>
  <b-modal
    id="add-disposal"
    title="Add Disposal Request"
    size="lg"
    ref="modal"
    ok-title="Add"
    @ok="handleOk"
    @show="onReset"
    :cancel-disabled="disableConfirmButtons"
    :ok-disabled="disableConfirmButtons"
    :no-close-on-backdrop="true"
    centered
  >
    <loading
      :active.sync="isLoading"
      loader="spinner"
      color="#20A8D8"
      :is-full-page="false"
    />

    <b-form @submit.stop.prevent="handleSubmit" novalidate>
      <b-container fluid>
        <b-row class="my-2">
          <b-col sm="8">
            <b>ASSET DETAILS</b>
          </b-col>
        </b-row>
        <b-row class="my-12">
          <b-col lg="6" md="6" sm="12">
            <b-form-group label="Asset Type">
              <v-select
                name="Asset Type"
                class="style-chooser"
                label="text"
                placeholder=" - Please select - "
                :options="assetTypeOptions"
                :reduce="(assetType) => assetType.value"
                v-model="selAssetType"
                v-validate="'selectRequired'"
                @input="onChangeAssetType"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    No results found for
                    <em>
                      <strong>{{ search }}</strong>
                    </em>
                  </template>
                  <em :style="{ opacity: 0.5 }" v-else>
                    Start typing to search for a company
                  </em>
                </template>
              </v-select>
              <span v-show="errors.has('Asset Type')" class="help-block">
                {{ errors.first("Asset Type") }}
              </span>
            </b-form-group>
          </b-col>

          <b-col lg="6" md="6" sm="12">
            <b-form-group
              label="Asset Code"
              label-for="Asset Code"
              description="List of assets that are marked as Damaged"
            >
              <AsyncPaginatedSelect
                name="Asset Code"
                cache-id="disposal-asset-code"
                :fetch-page="fetchAssetCodePage"
                :cache-key="(selAssetType && selAssetType.id) || ''"
                v-model="selAssetCode"
                @input="onChangeAssetCode"
                v-validate="'selectRequired'"
              />
              <span v-show="errors.has('Asset Code')" class="help-block">
                {{ errors.first("Asset Code") }}
              </span>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="my-12">
          <b-col lg="6" md="6" sm="12">
            <b-form-group label="Remarks" label-for="description">
              <b-form-textarea
                name="Description"
                type="text"
                v-model="form.reasonForDisposal"
                maxlength="200"
                v-validate="{
                  required: true,
                  regex: remarksRegex,
                }"
                :rows="3"
                placeholder="Description"
              />
              <span v-show="errors.has('Description')" class="help-block">
                {{ errors.first("Description") }}
              </span>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="my-2">
          <b-col sm="8">
            <b>LIST OF APPROVERS</b>
          </b-col>
        </b-row>
        <b-row class="my-12">
          <b-col lg="6" md="6" sm="12">
            <b-form-group
              label="Approver"
              description="The person you want to include to approve this disposal request"
            >
              <AsyncPaginatedSelect
                name="Approver"
                cache-id="disposal-approver"
                :fetch-page="fetchApproverPage"
                :cache-key="(selAssetCode && selAssetCode.currCompanyId) || ''"
                v-model="selApprover"
              />
              <span v-show="errors.has('Approver')" class="help-block">
                {{ errors.first("Approver") }}
              </span>
            </b-form-group>
          </b-col>
          <b-col lg="6" md="6" sm="12">
            <b-button
              class="btn-add-approver"
              @click.stop="onAddApprover"
              variant="primary"
              >Add Approver</b-button
            >
          </b-col>
        </b-row>
        <b-container v-if="form.approvers.length > 0">
          <b-row class="my-2">
            <b-col sm="8">
              <b>SELECTED APPROVERS</b>
            </b-col>
          </b-row>
          <b-row class="my-12">
            <b-col lg="8" md="8" sm="12">
              <b-table
                ref="approversTable"
                borderless
                small
                :items="form.approvers"
                :fields="fields"
                responsive
              >
                <template v-slot:cell(actions)="row">
                  <span class="text-nowrap">
                    <b-button
                      size="sm"
                      variant="danger"
                      @click.stop="onRemoveApprover(row.item.id)"
                      class="mr-1"
                    >
                      <em class="fa fa-trash"></em>
                    </b-button>
                  </span>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-container>
      </b-container>
    </b-form>
  </b-modal>
</template>

<script>
// Util
import { DisposalUtil } from "@/utils/disposalUtil";
import { ValidationUtil } from "@/utils/validationUtil";
import { DateUtil } from "@/utils/dateutil";

// DAO & API
import assetApi from "@/api/assetApi";
import userApi from "@/api/userApi";
import disposalRequestAPI from "@/api/disposalRequestApi";

// Components
import AsyncPaginatedSelect from "@/components/AsyncPaginatedSelect";

// Others
import EventBus from "@/shared/event-bus";
import config from "@/config/env-constants";
import Loading from "vue-loading-overlay";
import _ from "lodash";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  name: "add-disposal",
  components: {
    Loading,
    AsyncPaginatedSelect,
  },
  props: {
    assetTypeOptions: {
      type: Array,
      required: true,
    },
    loggedUser: {
      type: Object,
      required: true,
    },
    isMaintenance: {
      type: Boolean,
      required: true,
    },
    isSuperVisor: {
      type: Boolean,
      required: true,
    },
    isManager: {
      type: Boolean,
      required: true,
    },
    isSuperAdmin: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: {
        ...DisposalUtil.getDefaultDisposalObj(),
      },
      fields: [
        {
          key: "id",
          label: "Email",
        },
        {
          key: "name",
          label: "Name",
        },
        "actions",
      ],

      selAssetType: config.assetTypeDefaultValue,
      selAssetCode: config.assetCodeDefaultValue,
      selApprover: config.userDefaultValue,

      currUserId: this.$store.getters.loggedUser.id,
      loggedUserCompany: this.$store.getters.loggedUserCompany,

      // Check for loader
      isLoading: false,
    };
  },
  computed: {
    disableConfirmButtons() {
      return this.isLoading;
    },
    disposalRequestId() {
      return this.form.disposalRequestId;
    },
    remarksRegex() {
      return config.remarksRegex;
    },
  },
  methods: {
    onChangeAssetType(newVal) {
      this.selAssetType = newVal;
      this.selAssetCode = config.assetCodeDefaultValue;
    },
    async onChangeAssetCode() {
      this.selApprover = config.userDefaultValue;

      let companyId = this.selAssetCode && this.selAssetCode.currCompanyId;
      if (!companyId) {
        this.form.approvers = [];
        return;
      }
      if (this.form.approvers.length === 0) {
        return;
      }

      // Prune already-added approvers who no longer have access to the
      // newly selected asset's company. Needs the full approver pool (not
      // just a page) since any previously-added approver, not only ones on
      // the currently loaded page, may need to be dropped.
      let filterBy = { userType: { value: "Approver" }, isActive: "true" };
      let { data } = await userApi.getUsers(filterBy, config.view.ADMIN, this.currUserId);
      let validApproverIds = _.map(
        _.filter(data.users, (u) =>
          u.companyAccess ? u.companyAccess.some((x) => x.id === companyId) : false
        ),
        "id"
      );
      this.form.approvers = _.filter(this.form.approvers, (a) =>
        validApproverIds.includes(a.id)
      );
    },
    isAssetCodeCompanyRestricted() {
      return (
        (this.isMaintenance || this.isSuperVisor || this.isManager) &&
        (this.loggedUserCompany.hasParentCompany || this.isMaintenance)
      );
    },
    isSelectableAssetCode(asset) {
      let disposalStatuses = _.filter(
        Object.values(config.disposalStatus),
        (o) => o !== config.disposalStatus.CANCELLED
      );
      let repairStatuses = _.filter(
        Object.values(config.repairStatus),
        (o) => o !== config.repairStatus.CANCELLED
      );
      return (
        !disposalStatuses.includes(asset.disposalStatus) &&
        !repairStatuses.includes(asset.repairStatus)
      );
    },
    async fetchAssetCodePage(search, page) {
      if (!this.selAssetType || !this.selAssetType.id) {
        return { items: [], hasMore: false };
      }

      let filterBy = {
        assetType: { id: this.selAssetType.id },
        condition: "Damaged",
      };
      if (this.isAssetCodeCompanyRestricted()) {
        filterBy.currentCompany = { id: this.loggedUserCompany.id };
      }

      let { data } = await assetApi.getAssets(filterBy, config.view.ADMIN, this.currUserId, {
        search,
        page,
        pageSize: 20,
      });

      let items = _.filter(data.assets, (asset) => this.isSelectableAssetCode(asset)).map(
        (asset) => ({
          value: {
            id: asset.assetCode,
            currCompanyId: asset.currCompanyId,
            disposalStatus: asset.disposalStatus,
            repairStatus: asset.repairStatus,
            details: asset.details,
          },
          text: asset.assetCode,
        })
      );

      return { items, hasMore: !!(data.pageInfo && data.pageInfo.hasMore) };
    },
    async fetchApproverPage(search, page) {
      if (!this.selAssetCode || !this.selAssetCode.currCompanyId) {
        return { items: [], hasMore: false };
      }
      let companyId = this.selAssetCode.currCompanyId;

      let filterBy = { userType: { value: "Approver" }, isActive: "true" };
      let { data } = await userApi.getUsers(filterBy, config.view.ADMIN, this.currUserId, {
        search,
        page,
        pageSize: 20,
      });

      let approvers = _.filter(data.users, (u) =>
        u.companyAccess ? u.companyAccess.some((x) => x.id === companyId) : false
      );
      let items = _.map(approvers, (user) => ({
        value: {
          id: user.id,
          name: user.firstName + " " + user.lastName,
          companyId: user.companyId,
          type: user.type,
          companyAccess: user.companyAccess,
        },
        text: user.firstName + " " + user.lastName,
      }));

      return { items, hasMore: !!(data.pageInfo && data.pageInfo.hasMore) };
    },
    onAddApprover() {
      if (
        this.selApprover.id != null &&
        !this.form.approvers.some((approver) => approver.id === this.selApprover.id)
      ) {
        this.form.approvers.push(this.selApprover);
      }
    },
    onRemoveApprover(id) {
      this.form.approvers = this.form.approvers.filter((approver) => approver.id !== id);
    },
    cleanupFormFields() {
      // Removes excess whitespace
      this.form.reasonForDisposal = ValidationUtil.removeExcessWhiteSpace(
        this.form.reasonForDisposal
      );
    },
    generateDisposal(form, assetCodeObj, assetTypeObj) {
      let currTimestamp = DateUtil.getCurrentTimestamp();

      return {
        ...form,
        createdBy: this.currUserId,
        disposalRequestId: "DR" + currTimestamp,
        assetDetails: {
          assetType: assetTypeObj.name,
          assetCode: assetCodeObj.id,
          details: assetCodeObj.details,
        },
        dateCreated: currTimestamp,
        approverIds: form.approvers.map((approver) => approver.id),
        companyId: assetCodeObj.currCompanyId,
      };
    },
    getParam() {
      let disposal = this.generateDisposal(
        this.form,
        this.selAssetCode,
        this.selAssetType
      );

      return {
        currUserId: this.currUserId,
        disposal: disposal,
      };
    },
    async handleOk(evt) {
      // Prevent modal from closing
      evt.preventDefault();

      this.cleanupFormFields();

      if (this.form.approvers.length === 0) {
        this.$toaster.warning("Please add approver.");
        return;
      }

      let isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$toaster.warning("Please address the field/s with invalid input.");
        return;
      }

      // show loading indicator
      this.isLoading = true;

      await this.handleSubmit();

      // hide loading indicator
      this.isLoading = false;
    },
    async handleSubmit() {
      // show loading indicator
      this.isLoading = true;

      try {
        let param = this.getParam();
        let { data } = await disposalRequestAPI.saveDisposalRequest(param);

        if (data.isSuccess) {
          this.$toaster.success(data.message);
          EventBus.$emit("onCloseDisposalRequest", data.disposalRequest);
          this.$refs.modal.hide();
        } else {
          this.$toaster.error(
            `Error saving Disposal Request "${this.disposalRequest}". Please try again.`
          );
        }
      } catch (_error) {
        this.$toaster.error(
          `Error saving Disposal Request "${this.disposalRequest}". Please try again.`
        );
      }

      // hide loading indicator
      this.isLoading = false;
    },
    onReset() {
      this.form = {
        ...DisposalUtil.getDefaultDisposalObj(),
      };

      this.selAssetType = config.assetTypeDefaultValue;
      this.selAssetCode = config.assetCodeDefaultValue;
      this.selApprover = config.userDefaultValue;
      this.form.approvers = [];

      // reset validation
      this.$validator.reset();
      this.errors.clear();
    },
  },
};
</script>
